import { logger } from '@/utils/logging'
import { storage } from '@/utils/storage'
import { useCallback, useEffect, useState } from 'react'

export type DensityType = 'default' | 'comfortable' | 'compact'

interface UseTableDensityProps {
  defaultDensity?: DensityType
}

const STORAGE_KEY = 'table:density'

const safeJsonParse = ({
  jsonString,
  defaultValue = 'default' as DensityType,
}: {
  jsonString?: string
  defaultValue?: DensityType
}) => {
  if (!jsonString) return defaultValue
  try {
    return JSON.parse(jsonString) as DensityType
  } catch (error) {
    logger.error('Unable to parse localStorage density state', error)
    return defaultValue
  }
}

export const useTableDensity = ({
  defaultDensity = 'default',
}: UseTableDensityProps = {}) => {
  const getInitialDensity = (): DensityType => {
    const savedDensity = storage(STORAGE_KEY).getRaw()
    const initialDensity = safeJsonParse({
      jsonString: savedDensity,
      defaultValue: defaultDensity,
    })
    storage(STORAGE_KEY).set(JSON.stringify(initialDensity))
    return initialDensity
  }

  const [density, setDensity] = useState<DensityType>(() => getInitialDensity())

  const setDensityState = useCallback((newDensity: DensityType) => {
    storage(STORAGE_KEY).set(JSON.stringify(newDensity))
    setDensity(newDensity)
  }, [])

  useEffect(() => {
    const handleStorageChange = () => {
      const localState = storage(STORAGE_KEY).getRaw()
      const parsedLocalState = safeJsonParse({
        jsonString: localState,
        defaultValue: undefined,
      })
      if (parsedLocalState && localState !== JSON.stringify(density)) {
        setDensityState(parsedLocalState)
      }
    }

    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [setDensityState, density])

  return {
    density,
    setDensity: setDensityState,
  }
}
